import * as VoiceService from '@/admin/services/VoiceService'
import {
  CtiEventSource,
  CtiInteractionTypeEnum,
  LandingPageEnum,
  type CtiService,
  type CtiServiceWithInit,
  CtiStatus,
  CtiStatusColor,
  ctiStatusColorMap,
  type AgentModel,
  type CallInteractionDTOV2,
  type CallRequestBody,
  type CtiCallInfo,
  type IFrameStyle,
  type InboundCallConfigurationDTOV2,
  type OutboundCallConfigurationDTOV2,
  type ProviderGeneralConfigurationDTOV2,
  CtiOutboundCallSource,
  VoiceProvider,
} from '@/common/types/cti'
import { defineStore } from 'pinia'
import { computed, type Ref, ref } from 'vue'
import { AuthoritiesEnum } from '@/common/types/session'
import { useSessionStore } from '@/common/stores/sessionStore'
import { useLocalStorage } from '@vueuse/core'

export const dummyCtiService: CtiService = {
  outboundCallFromEverDesk() {
    /* Dummy == NoOp */
  },
  release() {
    /* Dummy == NoOp */
  },
  reload() {
    /* Dummy == NoOp */
  },
  setAgentStatus() {
    /* Dummy == NoOp */
  },
}

const blankURL = new URL('about:blank')

export const IFRAME_BASE_INNER_PADDING_PX = 48
export const IFRAME_BASE_TOP_PADDING_PX = 48
export const CASE_TOOLBAR_HEIGHT_PX = 46

export const useCtiStore = defineStore('cti', () => {
  const sessionStore = useSessionStore()

  const activeProvider: Ref<VoiceProvider> = ref(VoiceProvider.NONE)
  const token: Ref<string> = ref('')
  const toReinitialized: Ref<boolean> = ref(false)
  const connectable: Ref<boolean> = ref(false)
  const voiceIsInitialized: Ref<boolean> = ref(false)
  const dialogVisibility: Ref<boolean> = ref(false)
  const dialogStateSource: Ref<CtiEventSource> = ref(CtiEventSource.Automatic)
  const callerPhone: Ref<string | null> = ref('')
  const provider: Ref<CtiService | CtiServiceWithInit> = ref(dummyCtiService)
  const idGroup: Ref<string | null> = ref(null)
  const inboundCallConfiguration: Ref<InboundCallConfigurationDTOV2 | null> = ref(null)
  const outboundCallConfiguration: Ref<OutboundCallConfigurationDTOV2 | null> = ref(null)
  const url: Ref<URL> = ref(blankURL)
  const inboundCallsMap: Ref<Map<string, InboundCallConfigurationDTOV2>> = ref(new Map())
  const outboundCallsMap: Ref<Map<string, OutboundCallConfigurationDTOV2>> = ref(new Map())
  const outboundCallSource: Ref<CtiOutboundCallSource | null> = ref(null)
  const urlString: Ref<string> = ref('')
  const calledPhone: Ref<string | null> = ref(null)
  const customerId: Ref<number | null> = ref(null)
  const direction: Ref<CtiInteractionTypeEnum | null> = ref(null)
  const updateAtEnd: Ref<boolean> = ref(false)
  const landingPage: Ref<LandingPageEnum> = ref(LandingPageEnum.TICKET_HANDLING_SCREEN)
  const status: Ref<CtiStatus> = ref(CtiStatus.DISCONNECTED)
  const agentLogin: Ref<string | null> = ref(null)
  const ticketId: Ref<number | null> = ref(null)
  const userToUserInformation: Ref<string | undefined> = ref()
  const iframeStyle: Ref<IFrameStyle> = ref({
    narrowWidth: '320px',
    wideWidth: '560px',
    height: '200vh',
    innerPadding: `${IFRAME_BASE_INNER_PADDING_PX}px`,
    topPadding: `${IFRAME_BASE_TOP_PADDING_PX}px`,
  })

  function setIframeTopPadding(hasCaseToolbar: boolean) {
    const paddingPx = hasCaseToolbar ? IFRAME_BASE_TOP_PADDING_PX + CASE_TOOLBAR_HEIGHT_PX : IFRAME_BASE_TOP_PADDING_PX
    iframeStyle.value.topPadding = `${paddingPx}px`
  }
  const generalConfiguration: Ref<ProviderGeneralConfigurationDTOV2 | undefined> = ref(undefined)
  const iframeUrlUpdate: Ref<boolean> = ref(false)
  const incorrectUser: Ref<boolean> = ref(false)

  const isOutboundCallFromApplication = computed(() => outboundCallSource.value === CtiOutboundCallSource.APPLICATION)

  const idCall = useLocalStorage<string | null>('idCall', null)
  const ctiCallInfo = ref<CtiCallInfo | null>(null)

  const hasOngoingUnlinkedCtiCall = computed(() => {
    const info = ctiCallInfo.value
    if (!info?.requestInfo) {
      return false
    }

    if (info.isCallV2) {
      return !(info.requestInfo as CallInteractionDTOV2).caseId
    }

    return !(info.requestInfo as CallRequestBody).ticketId
  })

  function statusColor(): CtiStatusColor {
    return ctiStatusColorMap.get(status.value) ?? CtiStatusColor.black
  }

  function toggleDialog(source: CtiEventSource) {
    dialogVisibility.value = !dialogVisibility.value
    dialogStateSource.value = source
  }

  function showDialog(source: CtiEventSource) {
    dialogVisibility.value = true
    dialogStateSource.value = source
  }

  function hideDialog(source: CtiEventSource) {
    dialogVisibility.value = false
    dialogStateSource.value = source
  }

  function newInboundCall(
    newCallerPhone: string,
    newIdGroup: string | null,
    newCalledPhone: string | null,
    newIdCall: string | null,
    newUpdateAtEnd: boolean,
    newUuiData?: string,
  ) {
    direction.value = CtiInteractionTypeEnum.INBOUND
    showDialog(CtiEventSource.Automatic)
    callerPhone.value = newCallerPhone
    idGroup.value = newIdGroup
    calledPhone.value = newCalledPhone
    idCall.value = newIdCall
    updateAtEnd.value = newUpdateAtEnd
    userToUserInformation.value = newUuiData
  }

  function newInboundCallV2(
    newCallerPhone: string,
    newInboundCallConfiguration: InboundCallConfigurationDTOV2 | null,
    newCalledPhone: string | null,
    newIdCall: string | null,
    newUpdateAtEnd: boolean,
    newUuiData?: string,
  ) {
    direction.value = CtiInteractionTypeEnum.INBOUND
    showDialog(CtiEventSource.Automatic)
    callerPhone.value = newCallerPhone
    inboundCallConfiguration.value = newInboundCallConfiguration
    calledPhone.value = newCalledPhone
    idCall.value = newIdCall
    updateAtEnd.value = newUpdateAtEnd
    userToUserInformation.value = newUuiData
  }

  function newOutboundCallFromEverDesk(
    newOutboundCallConfiguration: UUID,
    newCalledPhone: string,
    newCustomerId: number | null,
    newIdTicket: number | null,
  ) {
    direction.value = CtiInteractionTypeEnum.OUTBOUND
    showDialog(CtiEventSource.Automatic)
    outboundCallConfiguration.value = [...outboundCallsMap.value.values()].find((aConf) => aConf.id === newOutboundCallConfiguration) ?? null
    calledPhone.value = newCalledPhone
    customerId.value = newCustomerId
    ticketId.value = newIdTicket
    updateAtEnd.value = true
  }

  function clearCustomerCtiSession() {
    ctiCallInfo.value = null
  }

  function endCall(newIdGroup: string | null, newCalledPhone: string | null, newIdCall: string | null, updateStatus: boolean = true) {
    idGroup.value = newIdGroup
    calledPhone.value = newCalledPhone
    idCall.value = newIdCall
    if (updateStatus) {
      status.value = CtiStatus.AVAILABLE
    }
  }

  function release() {
    toReinitialized.value = true

    provider.value.release()
    provider.value = dummyCtiService
  }

  function setAgentStatus(state: string, breakCode?: string) {
    provider.value.setAgentStatus(state, breakCode)
  }

  async function initCtiConfig(initProviderFunction: () => Promise<void>) {
    if (!sessionStore.getAuthoritySet.has(AuthoritiesEnum.CTI_ACCESS)) {
      connectable.value = false
      voiceIsInitialized.value = true
      return
    }

    try {
      await initProviderFunction()
    } catch (e) {
      console.error(e)
      connectable.value = false
    }
  }

  async function getAgentLogin() {
    try {
      const agentData: AgentModel | null = await VoiceService.getAgentLogin()
      agentLogin.value = agentData?.providerLogin ?? null
    } catch (message) {
      console.error(message)
    }
  }

  async function getDefaultOutboundCallConfiguration() {
    return (
      [...outboundCallsMap.value.values()].find((configuration) => configuration.defaultSkill === generalConfiguration.value?.fallbackSkill) ?? null
    )
  }

  return {
    activeProvider,
    token,
    toReinitialized,
    connectable,
    voiceIsInitialized,
    dialogVisibility,
    dialogStateSource,
    callerPhone,
    provider,
    idGroup,
    inboundCallConfiguration,
    outboundCallConfiguration,
    calledPhone,
    customerId,
    url,
    inboundCallsMap,
    outboundCallsMap,
    urlString,
    idCall,
    direction,
    updateAtEnd,
    landingPage,
    status,
    agentLogin,
    ticketId,
    statusColor,
    toggleDialog,
    showDialog,
    hideDialog,
    newInboundCall,
    newInboundCallV2,
    newOutboundCallFromEverDesk,
    clearCustomerCtiSession,
    endCall,
    release,
    initCtiConfig,
    iframeStyle,
    setIframeTopPadding,
    incorrectUser,
    generalConfiguration,
    iframeUrlUpdate,
    userToUserInformation,
    setAgentStatus,
    getAgentLogin,
    ctiCallInfo,
    dummyCtiService,
    getDefaultOutboundCallConfiguration,
    outboundCallSource,
    isOutboundCallFromApplication,
    hasOngoingUnlinkedCtiCall,
  }
})
